// sapi-admin/src/pages/RateSettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, setDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon, 
  TrendingUp, 
  Save, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Zap,
  Pi,
  DollarSign,
  RotateCcw,
  Info,
  ArrowUpRight,
  Globe,
  Sparkles,
  Lock,
  Unlock
} from 'lucide-react';
import axios from 'axios';

// ─── Glass Panel ──────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ 
    background: "rgba(17,24,39,.85)", 
    border: "1px solid rgba(255,255,255,.06)", 
    borderRadius: 16, 
    backdropFilter: "blur(16px)", 
    ...style 
  }}>
    {children}
  </div>
);

// ─── Input Field with Label ─────────────────────────────────────────────
const RateInput = ({ 
  label, 
  value, 
  onChange, 
  min = 0, 
  max = 1000, 
  step = 1,
  prefix = '',
  suffix = '',
  color = 'purple',
  disabled = false,
  icon: Icon,
  helperText = '',
  locked = false
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const colorMap = {
    purple: { border: 'border-purple-500/50', focus: 'focus:border-purple-500', text: 'text-purple-400' },
    cyan: { border: 'border-cyan-500/50', focus: 'focus:border-cyan-500', text: 'text-cyan-400' },
    amber: { border: 'border-amber-500/50', focus: 'focus:border-amber-500', text: 'text-amber-400' },
    emerald: { border: 'border-emerald-500/50', focus: 'focus:border-emerald-500', text: 'text-emerald-400' },
    rose: { border: 'border-rose-500/50', focus: 'focus:border-rose-500', text: 'text-rose-400' },
  };
  const c = colorMap[color] || colorMap.purple;

  const handleChange = (e) => {
    const val = e.target.value;
    setLocalValue(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num >= min && num <= max) {
      onChange(num);
    }
  };

  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1.5 flex items-center gap-2">
        {Icon && <Icon size={14} className={c.text} />}
        {label}
        {locked && (
          <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full flex items-center gap-1">
            <Lock size={10} /> Auto
          </span>
        )}
      </label>
      <div className={`relative rounded-xl transition-all duration-200 ${isFocused ? `ring-2 ${c.border}` : ''}`}>
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={localValue}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={`w-full ${prefix ? 'pl-16' : 'pl-4'} pr-4 py-3 bg-slate-800/50 border ${isFocused ? c.border : 'border-slate-600'} rounded-xl text-white text-lg font-bold focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${disabled ? 'opacity-60' : ''}`}
        />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">
            {suffix}
          </span>
        )}
      </div>
      {helperText && <p className="text-[10px] text-slate-500 mt-1">{helperText}</p>}
    </div>
  );
};

// ─── Fetch live Pi price (works in dev AND prod) ────────────────────────
const fetchLivePiPrice = async () => {
  const USDT_TO_FCFA = 600;
  const sources = [
    {
      name: 'OKX',
      url: 'https://www.okx.com/api/v5/market/ticker?instId=PI-USDT',
      parser: (data) => {
        const price = parseFloat(data.data?.[0]?.last);
        return !isNaN(price) && price > 0 ? price : null;
      }
    },
    {
      name: 'MEXC',
      url: 'https://api.mexc.com/api/v3/ticker/price?symbol=PIUSDT',
      parser: (data) => {
        const price = parseFloat(data.price);
        return !isNaN(price) && price > 0 ? price : null;
      }
    },
    {
      name: 'Gate.io',
      url: 'https://api.gateio.ws/api/v4/spot/tickers?currency_pair=PI_USDT',
      parser: (data) => {
        if (data && data[0] && data[0].last) {
          const price = parseFloat(data[0].last);
          return !isNaN(price) && price > 0 ? price : null;
        }
        return null;
      }
    },
    {
      name: 'CoinGecko',
      url: 'https://api.coingecko.com/api/v3/simple/price?ids=pi-network&vs_currencies=usd',
      parser: (data) => {
        const price = data['pi-network']?.usd;
        return price && price > 0 ? price : null;
      }
    }
  ];

  for (const source of sources) {
    try {
      const response = await axios.get(source.url, { timeout: 5000 });
      const price = source.parser(response.data);
      if (price) {
        return { usdt: price, fcfa: price * USDT_TO_FCFA, source: source.name };
      }
    } catch (e) {
      // Silent
    }
  }
  return null;
};

// ─── Main Component ──────────────────────────────────────────────────────
export default function RateSettings() {
  // ─── State ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [sellPiRate, setSellPiRate] = useState(60);
  const [sellPiCommission, setSellPiCommission] = useState(0.01);
  const [sellPiMode, setSellPiMode] = useState('dynamic');
  const [margin, setMargin] = useState(0.10);

  const [eneoRate, setEneoRate] = useState(150);

  const [saving, setSaving] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalValues, setOriginalValues] = useState({});

  // ─── Live market rate ──────────────────────────────────────────────────
  const [liveMarketRate, setLiveMarketRate] = useState(null);
  const [marketSource, setMarketSource] = useState(null);
  const [loadingMarket, setLoadingMarket] = useState(true);
  const [marketError, setMarketError] = useState(false);

  const isAutoAdjustingRef = useRef(false);

  // ─── Load Data from Firestore ──────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const unsubscribeSellPi = onSnapshot(
      doc(db, 'settings', 'exchangeRate'),
      (snap) => {
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          setSellPiRate(data.rate ?? 60);
          setSellPiCommission(data.commission ?? 0.01);
          setSellPiMode(data.mode ?? 'dynamic');
          setMargin(data.margin ?? 0.10);
          setOriginalValues(prev => ({ 
            ...prev, 
            sellPiRate: data.rate ?? 60, 
            sellPiCommission: data.commission ?? 0.01, 
            sellPiMode: data.mode ?? 'dynamic',
            margin: data.margin ?? 0.10
          }));
        }
        setLoading(false);
      },
      () => setLoading(false)
    );

    const unsubscribeEneo = onSnapshot(
      doc(db, 'settings', 'eneoRate'),
      (snap) => {
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          setEneoRate(data.rate ?? 150);
          setOriginalValues(prev => ({ ...prev, eneoRate: data.rate ?? 150 }));
        }
        setLoading(false);
      },
      () => setLoading(false)
    );

    const loadLastUpdate = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'meta'));
        if (snap.exists() && snap.data().lastRateUpdate) {
          setLastUpdate(new Date(snap.data().lastRateUpdate));
        }
      } catch (error) {}
    };
    loadLastUpdate();

    return () => {
      mounted = false;
      unsubscribeSellPi();
      unsubscribeEneo();
    };
  }, []);

  // ─── Fetch live market rate & auto-adjust ─────────────────────────────
  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const fetchAndAdjust = async () => {
      if (!mounted) return;
      
      setLoadingMarket(true);
      setMarketError(false);
      
      try {
        const result = await fetchLivePiPrice();
        if (!mounted) return;
        
        if (result) {
          setLiveMarketRate(result);
          setMarketSource(result.source);
          
          if (sellPiMode === 'dynamic' && !isAutoAdjustingRef.current) {
            isAutoAdjustingRef.current = true;
            const suggestedRate = Math.round(result.fcfa * (1 + margin));
            if (Math.abs(suggestedRate - sellPiRate) > 1 && suggestedRate > 0) {
              setSellPiRate(suggestedRate);
              setHasChanges(true);
            }
            setTimeout(() => { isAutoAdjustingRef.current = false; }, 500);
          }
        } else {
          setMarketError(true);
        }
      } catch (error) {
        if (mounted) setMarketError(true);
      } finally {
        if (mounted) setLoadingMarket(false);
      }
    };

    fetchAndAdjust();
    intervalId = setInterval(fetchAndAdjust, 5 * 60 * 1000);

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [sellPiMode, margin]);

  // ─── Detect changes ────────────────────────────────────────────────────
  useEffect(() => {
    const hasSellPiChanges = 
      sellPiRate !== originalValues.sellPiRate ||
      sellPiCommission !== originalValues.sellPiCommission ||
      sellPiMode !== originalValues.sellPiMode ||
      margin !== originalValues.margin;
    const hasEneoChanges = eneoRate !== originalValues.eneoRate;
    setHasChanges(hasSellPiChanges || hasEneoChanges);
  }, [sellPiRate, sellPiCommission, sellPiMode, margin, eneoRate, originalValues]);

  // ─── Save Settings ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      return;
    }

    if (sellPiRate < 1 || sellPiRate > 1000) {
      toast.error('Sell Pi rate must be between 1 and 1000 FCFA');
      return;
    }
    if (eneoRate < 1 || eneoRate > 1000) {
      toast.error('ENEO rate must be between 1 and 1000 FCFA');
      return;
    }
    if (margin < 0.05 || margin > 0.30) {
      toast.error('Margin must be between 5% and 30%');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Saving settings...');

    try {
      await updateDoc(doc(db, 'settings', 'exchangeRate'), {
        rate: Number(sellPiRate),
        commission: Number(sellPiCommission),
        mode: sellPiMode,
        margin: Number(margin),
        updatedAt: new Date().toISOString()
      });

      await updateDoc(doc(db, 'settings', 'eneoRate'), {
        rate: Number(eneoRate),
        updatedAt: new Date().toISOString()
      });

      await setDoc(doc(db, 'settings', 'meta'), {
        lastRateUpdate: new Date().toISOString(),
        updatedBy: 'admin'
      }, { merge: true });

      setLastUpdate(new Date());
      setOriginalValues({
        sellPiRate,
        sellPiCommission,
        sellPiMode,
        margin,
        eneoRate
      });
      setHasChanges(false);

      toast.dismiss(toastId);
      toast.success('✅ All settings saved successfully!', { autoClose: 3000 });
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('❌ Failed to save settings', { autoClose: 5000 });
    } finally {
      setSaving(false);
    }
  };

  // ─── Reset to Defaults ─────────────────────────────────────────────────
  const handleReset = () => {
    if (window.confirm('Reset all rates to default values?')) {
      setSellPiRate(60);
      setSellPiCommission(0.01);
      setSellPiMode('dynamic');
      setMargin(0.10);
      setEneoRate(150);
      toast.info('Values reset to defaults. Click Save to apply.');
    }
  };

  // ─── Manual adjust ─────────────────────────────────────────────────────
  const handleManualAdjust = () => {
    if (liveMarketRate && liveMarketRate.fcfa > 0) {
      const suggestedRate = Math.round(liveMarketRate.fcfa * (1 + margin));
      if (suggestedRate > 0 && suggestedRate < 500) {
        setSellPiRate(suggestedRate);
        toast.info(`✅ Rate adjusted to ${suggestedRate} FCFA (market: ${Math.round(liveMarketRate.fcfa)} FCFA, margin: ${Math.round(margin * 100)}%)`);
      } else {
        toast.warning('Market unstable, adjust manually');
      }
    } else {
      toast.warning('Unable to fetch market rate');
    }
  };

  // ─── Loading ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="w-11 h-11 rounded-full border-4 border-amber-500/30 border-t-amber-500 animate-spin" />
        <p className="text-sm text-slate-500">Loading Rate Settings…</p>
      </div>
    );
  }

  const isRateLocked = sellPiMode === 'dynamic';

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-8">
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse-dot{0%,100%{opacity:1}50%{opacity:.3}}
      `}</style>

      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-emerald-500/20">
        <CheckCircle size={12} />
        <span>Loaded from Firestore</span>
      </div>

      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center ring-1 ring-amber-500/30">
              <SettingsIcon size={20} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">Exchange Rate Settings</h1>
              <p className="text-sm text-slate-400">Manage Pi → FCFA exchange rates</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap bg-slate-800/50 px-4 py-2 rounded-xl border border-slate-700/50">
          <div className="text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Sapi Rate</p>
            <p className="text-sm font-bold text-purple-400">{sellPiRate} FCFA</p>
            {isRateLocked && <p className="text-[8px] text-amber-400/70">Auto</p>}
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Market</p>
            {loadingMarket ? (
              <RefreshCw size={14} className="text-slate-500 animate-spin" />
            ) : liveMarketRate ? (
              <p className="text-sm font-bold text-emerald-400">{Math.round(liveMarketRate.fcfa)} FCFA</p>
            ) : (
              <p className="text-xs text-rose-400">Unavailable</p>
            )}
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Margin</p>
            <p className="text-sm font-bold text-amber-400">{Math.round(margin * 100)}%</p>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div className="text-center">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Updated</p>
            <p className="text-xs font-medium text-slate-300">
              {lastUpdate?.toLocaleTimeString() || '--:--'}
            </p>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/20">
              <AlertCircle size={12} />
              <span>Unsaved</span>
            </div>
          )}
        </div>
      </div>

      {/* ─── Live Market Rate Banner ────────────────────────────────────── */}
      <div className={`p-4 rounded-xl border ${loadingMarket ? 'bg-slate-800/30 border-slate-700/50' : marketError || !liveMarketRate ? 'bg-rose-500/10 border-rose-500/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${loadingMarket ? 'bg-slate-700' : marketError || !liveMarketRate ? 'bg-rose-500/20' : 'bg-emerald-500/20'}`}>
              {loadingMarket ? (
                <RefreshCw size={14} className="text-slate-400 animate-spin" />
              ) : marketError || !liveMarketRate ? (
                <AlertCircle size={14} className="text-rose-400" />
              ) : (
                <Globe size={14} className="text-emerald-400" />
              )}
            </div>
            <div>
              <p className="text-xs text-slate-400 flex items-center gap-2">
                <span className="font-semibold">📊 Live Pi Rate</span>
                {loadingMarket && <span className="text-slate-500">(loading...)</span>}
                {liveMarketRate && <span className="text-emerald-400">● Live</span>}
              </p>
              <div className="flex items-center gap-4 mt-1 flex-wrap">
                {liveMarketRate ? (
                  <>
                    <span className="text-lg font-bold text-white">
                      {Math.round(liveMarketRate.fcfa).toLocaleString()} FCFA
                    </span>
                    <span className="text-xs text-slate-400">
                      1 π = ${liveMarketRate.usdt.toFixed(4)} USD
                    </span>
                    <span className="text-[10px] text-slate-500 bg-slate-700/30 px-2 py-0.5 rounded-full">
                      Source: {marketSource || 'Aggregated'}
                    </span>
                    <span className="text-[10px] text-emerald-400/70 flex items-center gap-1">
                      <Sparkles size={10} /> Updates every 5min
                    </span>
                    {isRateLocked && (
                      <span className="text-[10px] text-amber-400/80 bg-amber-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Lock size={10} /> Auto-adjusted
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-sm text-rose-400">
                    {marketError ? '⚠️ Unable to fetch market rate' : 'Waiting for data...'}
                  </span>
                )}
              </div>
            </div>
          </div>
          {liveMarketRate && (
            <button
              onClick={handleManualAdjust}
              className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-500/30 transition flex items-center gap-1"
            >
              <RefreshCw size={12} /> Adjust
            </button>
          )}
        </div>
      </div>

      {/* ─── Sell Pi Rate Card ───────────────────────────────────────────── */}
      <GlassPanel style={{ border: '1px solid rgba(139,92,246,.2)' }}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center ring-1 ring-purple-500/30">
              <Pi size={20} className="text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                Sell Pi - Buy Rate
                <span className={`text-[9px] px-2 py-0.5 rounded-full ${sellPiMode === 'dynamic' ? 'bg-purple-500/20 text-purple-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {sellPiMode === 'dynamic' ? '🔄 Dynamic' : '🔒 Fixed'}
                </span>
              </h3>
              <p className="text-xs text-slate-500">1 Pi = ? FCFA (for sellers)</p>
            </div>
            {liveMarketRate && isRateLocked && (
              <div className="ml-auto text-right">
                <p className="text-[10px] text-slate-400">Calculated automatically</p>
                <p className="text-xs text-emerald-400 font-medium">
                  {Math.round(liveMarketRate.fcfa)} × {Math.round((1 + margin) * 100)}% = {sellPiRate} FCFA
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <RateInput
              label="Rate (FCFA)"
              value={sellPiRate}
              onChange={setSellPiRate}
              min={1}
              max={1000}
              step={1}
              suffix="FCFA"
              color="purple"
              icon={DollarSign}
              disabled={isRateLocked}
              locked={isRateLocked}
              helperText={isRateLocked && liveMarketRate ? `Auto: ${Math.round(liveMarketRate.fcfa)} × ${Math.round((1 + margin) * 100)}%` : 'Manual'}
            />

            <RateInput
              label="Commission (%)"
              value={Math.round(sellPiCommission * 100)}
              onChange={(val) => setSellPiCommission(val / 100)}
              min={0}
              max={10}
              step={0.5}
              suffix="%"
              color="amber"
              icon={TrendingUp}
              helperText="Transaction fee (recommended: 1-2%)"
            />

            <div>
              <label className="text-xs text-slate-400 block mb-1.5">Mode</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setSellPiMode('dynamic');
                    if (liveMarketRate) {
                      setSellPiRate(Math.round(liveMarketRate.fcfa * (1 + margin)));
                    }
                  }}
                  className={`px-4 py-3 rounded-xl font-semibold transition-all ${sellPiMode === 'dynamic' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30 ring-1 ring-purple-400' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'}`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Unlock size={16} />
                    <span className="text-sm">Dynamic</span>
                  </div>
                  <p className="text-[9px] opacity-70">Auto-adjusted</p>
                </button>
                <button
                  onClick={() => setSellPiMode('fixed')}
                  className={`px-4 py-3 rounded-xl font-semibold transition-all ${sellPiMode === 'fixed' ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30 ring-1 ring-purple-400' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'}`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Lock size={16} />
                    <span className="text-sm">Fixed</span>
                  </div>
                  <p className="text-[9px] opacity-70">Manual</p>
                </button>
              </div>
              <p className="text-[9px] text-slate-500 mt-1">
                {sellPiMode === 'dynamic' ? 'Rate adjusts automatically' : 'You manage the rate manually'}
              </p>
            </div>
          </div>

          {/* ─── Configurable Margin ────────────────────────────────────── */}
          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <RateInput
              label="Margin (%)"
              value={Math.round(margin * 100)}
              onChange={(val) => {
                const newMargin = val / 100;
                setMargin(newMargin);
                if (sellPiMode === 'dynamic' && liveMarketRate) {
                  setSellPiRate(Math.round(liveMarketRate.fcfa * (1 + newMargin)));
                }
              }}
              min={5}
              max={30}
              step={0.5}
              suffix="%"
              color="emerald"
              icon={TrendingUp}
              helperText={liveMarketRate ? `Calculated rate: ${Math.round(liveMarketRate.fcfa)} × ${Math.round((1 + margin) * 100)}% = ${Math.round(liveMarketRate.fcfa * (1 + margin))} FCFA` : 'Waiting for market...'}
            />
          </div>

          {/* Preview */}
          <div className="mt-4 p-3 bg-purple-500/5 border border-purple-500/10 rounded-xl">
            <div className="flex items-center justify-between text-sm flex-wrap gap-2">
              <span className="text-slate-400">1 π =</span>
              <span className="text-xl font-bold text-purple-400">{sellPiRate} FCFA</span>
              {liveMarketRate && (
                <span className="text-xs text-slate-500">
                  Market: {Math.round(liveMarketRate.fcfa)} FCFA • Margin: {Math.round(margin * 100)}% • Commission: {Math.round(sellPiCommission * 100)}%
                </span>
              )}
            </div>
            {sellPiMode === 'dynamic' && liveMarketRate && (
              <div className="flex items-center gap-1 text-[10px] text-purple-400/70 mt-1">
                <ArrowUpRight size={12} />
                <span>Auto-adjusted rate: {Math.round(liveMarketRate.fcfa)} + {Math.round(margin * 100)}% = {sellPiRate} FCFA</span>
              </div>
            )}
          </div>
        </div>
      </GlassPanel>

      {/* ─── ENEO Rate Card ───────────────────────────────────────────────── */}
      <GlassPanel style={{ border: '1px solid rgba(6,182,212,.2)' }}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center ring-1 ring-cyan-500/30">
              <Zap size={20} className="text-cyan-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                ENEO - Fixed Rate
                <span className="text-[9px] bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">🔒 Fixed</span>
              </h3>
              <p className="text-xs text-slate-500">1 Pi = ? FCFA (for top-ups)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <RateInput
              label="Rate (FCFA)"
              value={eneoRate}
              onChange={setEneoRate}
              min={1}
              max={1000}
              step={1}
              suffix="FCFA"
              color="cyan"
              icon={DollarSign}
              helperText="Fixed rate for ENEO top-ups"
            />

            <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-4 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <CheckCircle size={16} className="text-cyan-400" />
                  <span className="text-sm font-semibold text-cyan-400">FIXED RATE</span>
                </div>
                <p className="text-2xl font-bold text-cyan-400">{eneoRate} FCFA</p>
                <p className="text-[10px] text-slate-500 mt-1">Rate does not change automatically</p>
              </div>
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* ─── Actions ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`flex-1 py-4 rounded-xl font-bold flex items-center justify-center gap-2 text-lg transition-all ${saving || !hasChanges ? 'bg-slate-700/50 text-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:scale-[1.02]'}`}
        >
          {saving ? <><RefreshCw size={20} className="animate-spin" /> Saving...</> : <><Save size={20} /> {hasChanges ? 'Save' : 'No changes'}</>}
        </button>

        <button
          onClick={handleReset}
          className="px-6 py-4 bg-slate-700/50 text-slate-300 rounded-xl font-semibold hover:bg-slate-700 transition flex items-center justify-center gap-2"
        >
          <RotateCcw size={18} /> Reset
        </button>
      </div>

      {/* ─── Info Box ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-amber-400 mt-0.5" />
            <div>
              <p className="text-sm text-amber-400 font-medium">Affected modules</p>
              <ul className="text-xs text-slate-400 mt-2 space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                  <span className="text-purple-400 font-medium">Sell Pi</span>
                  <span className="text-slate-500">: Pi buy rate (mode {sellPiMode})</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                  <span className="text-cyan-400 font-medium">ENEO Top-up</span>
                  <span className="text-slate-500">: Fixed rate for top-ups</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={16} className="text-emerald-400 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-400 font-medium">Auto-adjustment</p>
              <p className="text-xs text-slate-400 mt-2">
                In <span className="text-emerald-300 font-medium">"Dynamic"</span> mode, the rate adjusts automatically every 5 minutes with a margin of {Math.round(margin * 100)}%.
              </p>
              <p className="text-[10px] text-slate-500 mt-1">
                Current market: {liveMarketRate ? `${Math.round(liveMarketRate.fcfa).toLocaleString()} FCFA` : 'unavailable'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Current Values Summary ────────────────────────────────────── */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6 flex-wrap">
            <div>
              <span className="text-xs text-slate-500">Sell Pi</span>
              <p className="text-sm font-bold text-purple-400">{sellPiRate} FCFA {isRateLocked && <span className="text-[8px] text-amber-400">(auto)</span>}</p>
            </div>
            <div className="w-px h-8 bg-slate-700/50" />
            <div>
              <span className="text-xs text-slate-500">Commission</span>
              <p className="text-sm font-bold text-purple-400">{Math.round(sellPiCommission * 100)}%</p>
            </div>
            <div className="w-px h-8 bg-slate-700/50" />
            <div>
              <span className="text-xs text-slate-500">Margin</span>
              <p className="text-sm font-bold text-amber-400">{Math.round(margin * 100)}%</p>
            </div>
            <div className="w-px h-8 bg-slate-700/50" />
            <div>
              <span className="text-xs text-slate-500">Mode</span>
              <p className="text-sm font-bold text-purple-400 uppercase">{sellPiMode}</p>
            </div>
            <div className="w-px h-8 bg-slate-700/50" />
            <div>
              <span className="text-xs text-slate-500">ENEO</span>
              <p className="text-sm font-bold text-cyan-400">{eneoRate} FCFA</p>
            </div>
            {liveMarketRate && (
              <>
                <div className="w-px h-8 bg-slate-700/50" />
                <div>
                  <span className="text-xs text-slate-500">Market</span>
                  <p className="text-sm font-bold text-emerald-400">{Math.round(liveMarketRate.fcfa)} FCFA</p>
                </div>
                <div className="w-px h-8 bg-slate-700/50" />
                <div>
                  <span className="text-xs text-slate-500">Real margin</span>
                  <p className={`text-sm font-bold ${sellPiRate > liveMarketRate.fcfa ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {Math.round((sellPiRate / liveMarketRate.fcfa - 1) * 100)}%
                  </p>
                </div>
              </>
            )}
          </div>
          {hasChanges && (
            <div className="flex items-center gap-1.5 text-xs text-amber-400">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              Unsaved changes
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}